import * as React from 'react';
import { BookingEntity } from 'Models/Entities';
import { FareTypeFee } from 'Models/Entities/BookingEntity';
import { EditType } from '../../BookingDetailedView';
import { WizardModal } from 'Views/Components/Modal/WizardModal';
import TripOptionCard from 'Views/Components/_HumanWritten/TripOptionCard/TripOptionCard';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { useLocationNamesFromBookingSummary } from '../../Hooks/useLocationNamesFromBookingSummary';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { EditOption } from './GetBookingEditOptions';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import If from 'Views/Components/If/If';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

// Edit types that move the booking to a different ferry trip, which is what triggers an
// alteration (EDIT) cutoff fee. Only these options get the "a fee may apply" notice.
const DATE_TIME_EDIT_TYPES: EditType[] = ['trip-time', 'event-date-time'];

// The fare type / seat option. Changing a passenger's fare type is what charges the alteration fee
// now that moving between fare types is no longer treated as a removal, so this is where the
// per-fare-type costs belong.
const FARE_TYPE_EDIT_TYPES: EditType[] = ['seating'];

interface FeeDirection {
	label: string;
	/** Pre-formatted fee for this leg, e.g. "10%" or "$15.00". */
	display: string;
	/** Short plain-English rule that triggers the fee, e.g. "If changed within 24 hours of departure". */
	rule?: string;
	/**
	 * True when this leg's cutoff waives the alteration fee for moving to an earlier (closer) trip.
	 * Exempt on any date, so we can advertise it unconditionally. Note this waives only the fee — a
	 * dearer trip still charges the fare difference — so the copy says "no alteration fee", not "free".
	 */
	earlierExempt: boolean;
	/**
	 * True when this leg's cutoff waives the alteration fee for moving to a later trip on the SAME
	 * day. Unlike the earlier waiver this is conditional on the target being same-day, so the copy
	 * advertises it as "same day" rather than promising any later move is fee-free.
	 */
	laterExempt: boolean;
}

/**
 * Turns a cutoff window in hours into a short, friendly phrase — whole days when it divides evenly
 * (e.g. 48 → "2 days"), otherwise hours (e.g. 24 → "24 hours").
 */
function formatCutoffWindow(hours: number): string {
	const rounded = Math.round(hours);
	if (rounded >= 24 && rounded % 24 === 0) {
		const days = rounded / 24;
		return `${days} ${days === 1 ? 'day' : 'days'}`;
	}
	return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
}

/**
 * Builds one row per direction (departure, and return when present) describing the alteration
 * fee that currently applies to changing that leg's date/time — the percentage when the cutoff
 * is percentage-based, otherwise the flat dollar amount — plus the short rule that triggers it.
 * Legs with no fee are omitted.
 */
function useDateTimeFeeDirections(booking: BookingEntity): FeeDirection[] {
	const thisLeg = useLocationNamesFromBookingSummary(booking.bookedSummary);
	const returnBooking = booking.returnBooking ?? booking.returnBookingFor;
	const returnLeg = useLocationNamesFromBookingSummary(returnBooking?.bookedSummary);

	const buildDisplay = (leg?: BookingEntity): string | undefined => {
		const percentage = leg?.alterationFeePercentage;
		if (percentage && percentage > 0) {
			return `${Number(percentage)}%`;
		}
		// Flat/static fee — no percentage, so show the dollar amount the customer is charged.
		const flatFee = leg?.alterationFee;
		if (flatFee && flatFee > 0) {
			return formatPriceDisplay(flatFee, false);
		}
		return undefined;
	};

	const buildRule = (leg?: BookingEntity): string | undefined => {
		// An any-time rule has no window to put a "within ..." on, and alterationCutoffHours reports 0
		// for one — indistinguishable from a genuine zero-hour rule, which is why the flag is carried
		// separately. Without it the line was dropped and the notice said only "a fee may apply",
		// never that changing the trip earlier would not avoid the fee.
		if (leg?.alterationCutoffAppliesAnytime) {
			return 'If changed at any time before departure';
		}
		const hours = leg?.alterationCutoffHours;
		if (hours && hours > 0) {
			return `If changed within ${formatCutoffWindow(hours)} of departure`;
		}
		return undefined;
	};

	const routeLabel = (start?: string, end?: string, fallback?: string) =>
		start && end ? `${start} → ${end}` : (fallback ?? '');

	const directions: FeeDirection[] = [];

	const thisDisplay = buildDisplay(booking);
	if (thisDisplay) {
		directions.push({
			label: routeLabel(thisLeg.startLocationName, thisLeg.endLocationName, 'Departure trip'),
			display: thisDisplay,
			rule: buildRule(booking),
			earlierExempt: booking.moveToEarlierTimeFeeExempt ?? false,
			laterExempt: booking.moveToLaterTimeFeeExempt ?? false,
		});
	}

	const returnDisplay = buildDisplay(returnBooking);
	if (returnBooking && returnDisplay) {
		directions.push({
			label: routeLabel(returnLeg.startLocationName, returnLeg.endLocationName, 'Return trip'),
			display: returnDisplay,
			rule: buildRule(returnBooking),
			earlierExempt: returnBooking.moveToEarlierTimeFeeExempt ?? false,
			laterExempt: returnBooking.moveToLaterTimeFeeExempt ?? false,
		});
	}

	return directions;
}

/** A fee as it reads on a row — a flat amount or a percentage. Null means nothing is charged. */
function formatFareTypeFee(amount: number | null, staticFee: boolean): string | null {
	if (amount == null || amount <= 0) return null;
	return staticFee ? formatPriceDisplay(amount, false) : `${Number(amount)}%`;
}

/** Every fare type on this booking, across both legs, with the dearer duplicates collapsed away. */
function useFareTypeFees(booking: BookingEntity): FareTypeFee[] {
	const returnBooking = booking.returnBooking ?? booking.returnBookingFor;
	const all = [...(booking.fareTypeFees ?? []), ...(returnBooking?.fareTypeFees ?? [])];

	// One row per fare type, not one per leg: the legs almost always match, and two identical rows
	// headed the same way reads as a mistake. Where they differ the dearer is kept, so the notice
	// never quotes less than the customer could be charged.
	const byClass = new Map<string, FareTypeFee>();
	all.forEach(fee => {
		const existing = byClass.get(fee.passengerClassId);
		if (!existing || (fee.alterationFeeAmount ?? 0) > (existing.alterationFeeAmount ?? 0)) {
			byClass.set(fee.passengerClassId, fee);
		}
	});

	return [...byClass.values()];
}

/**
 * What each fare type on the booking would cost to change, and to remove a passenger from.
 *
 * <p>Shown before anything is chosen. The booking-wide alteration fee is matched against every fare
 * type at once and names one rule, which on a booking carrying two fare types is not the answer for
 * either of them.</p>
 */
function FareTypeFeeNotice({ booking }: { booking: BookingEntity }) {
	const [expanded, setExpanded] = React.useState(false);
	const fees = useFareTypeFees(booking);

	// Nothing to say where no fare type carries a fee — a bare "a fee may apply" over a list of
	// zeroes puts customers off a change that would cost them nothing.
	const anyFee = fees.some(
		fee => (fee.alterationFeeAmount ?? 0) > 0 || (fee.removalFeeAmount ?? 0) > 0,
	);
	if (fees.length === 0 || !anyFee) {
		return null;
	}

	return (
		<MessageBox status="warning" slim noBottomMargin icon={null} className="mt-2">
			<button
				type="button"
				onClick={() => setExpanded(prev => !prev)}
				aria-expanded={expanded}
				data-test-id="edit-wizard-fare-type-fee-toggle"
				className="flex items-center gap-2 w-full text-left cursor-pointer"
			>
				<Icon name="info" useMaterialIcons classname="!text-[16px] text-amber-600 shrink-0" />
				<span className="text-[12px] font-medium text-amber-800 flex-1">
					A fee may apply, and it differs by fare type
				</span>
				<Icon
					name={expanded ? 'expand_less' : 'expand_more'}
					useMaterialIcons
					classname="!text-[18px] text-amber-600 shrink-0"
				/>
			</button>
			{expanded && (
				<ul className="mt-2 space-y-1.5 border-t border-amber-200 pt-2 list-none p-0 m-0">
					{fees.map(fee => {
						const alteration = formatFareTypeFee(fee.alterationFeeAmount, fee.alterationStaticFee);
						const removal = formatFareTypeFee(fee.removalFeeAmount, fee.removalStaticFee);
						return (
							<li key={fee.passengerClassId} className="text-[12px]">
								<span className="text-amber-900 font-medium">
									{fee.passengerClassName ?? 'No fare type'}
								</span>
								{fee.passengerCount > 1 && (
									<span className="text-amber-700"> &times;{fee.passengerCount}</span>
								)}
								<span className="block text-[11px] text-amber-700 mt-0.5 leading-snug">
									{alteration
										? `${alteration} to change this fare type`
										: 'No fee to change this fare type'}
								</span>
								<span className="block text-[11px] text-amber-700 leading-snug">
									{removal
										? `${removal} to remove one of these passengers`
										: 'No fee to remove one of these passengers'}
								</span>
							</li>
						);
					})}
				</ul>
			)}
		</MessageBox>
	);
}

function DateTimeFeeNotice({ booking }: { booking: BookingEntity }) {
	const [expanded, setExpanded] = React.useState(false);
	const directions = useDateTimeFeeDirections(booking);

	if (directions.length === 0) {
		return null;
	}

	// Lead with whichever alteration-fee exemptions apply to every leg carrying a fee, so the notice
	// reassures rather than a bare "a fee may apply" that puts customers off moving. We say "no
	// alteration fee" (not "free"): the waiver only drops the fee — a dearer trip still charges the
	// fare difference. The earlier waiver is unconditional; the later waiver is same-day only. A leg
	// without the exemption pulls its case back to the generic warning (conservative).
	const allEarlierExempt = directions.every(direction => direction.earlierExempt);
	const allLaterExempt = directions.every(direction => direction.laterExempt);
	let headline: string;
	if (allEarlierExempt && allLaterExempt) {
		headline =
			'No alteration fee for moving to an earlier trip, or a later trip on the same day — a fee may apply otherwise';
	} else if (allEarlierExempt) {
		headline = 'No alteration fee for moving to an earlier trip — a fee may apply if you move to a later time';
	} else if (allLaterExempt) {
		headline = 'No alteration fee for moving to a later trip on the same day — a fee may apply otherwise';
	} else {
		headline = 'A fee may apply when you change your trip date or time';
	}

	return (
		// icon={null}: the disclosure button below carries its own leading icon, so the box's would
		// be a second one on the same line.
		<MessageBox status="warning" slim noBottomMargin icon={null} className="mt-2">
			<button
				type="button"
				onClick={() => setExpanded(prev => !prev)}
				aria-expanded={expanded}
				data-test-id="edit-wizard-fee-notice-toggle"
				className="flex items-center gap-2 w-full text-left cursor-pointer"
			>
				<Icon name="info" useMaterialIcons classname="!text-[16px] text-amber-600 shrink-0" />
				<span className="text-[12px] font-medium text-amber-800 flex-1">{headline}</span>
				<Icon
					name={expanded ? 'expand_less' : 'expand_more'}
					useMaterialIcons
					classname="!text-[18px] text-amber-600 shrink-0"
				/>
			</button>
			{expanded && (
				<ul className="mt-2 space-y-1 border-t border-amber-200 pt-2 list-none p-0 m-0">
					{directions.map(direction => (
						<li key={direction.label} className="flex items-start justify-between gap-3 text-[12px]">
							<span className="min-w-0 flex flex-col">
								<span className="text-amber-900 flex items-center gap-1 min-w-0">
									<Icon
										name="directions_boat"
										useMaterialIcons
										classname="!text-[14px] text-amber-500 shrink-0"
									/>
									<span className="truncate">{direction.label}</span>
								</span>
								{direction.rule && (
									<span className="text-[11px] text-amber-700 mt-0.5 leading-snug">
										{direction.rule}
									</span>
								)}
								{direction.earlierExempt && (
									<span className="text-[11px] text-emerald-700 mt-0.5 leading-snug flex items-center gap-1">
										<Icon
											name="check_circle"
											useMaterialIcons
											classname="!text-[13px] text-emerald-500 shrink-0"
										/>
										No fee when moving to an earlier trip
									</span>
								)}
								{direction.laterExempt && (
									<span className="text-[11px] text-emerald-700 mt-0.5 leading-snug flex items-center gap-1">
										<Icon
											name="check_circle"
											useMaterialIcons
											classname="!text-[13px] text-emerald-500 shrink-0"
										/>
										No fee when moving to a later trip on the same day
									</span>
								)}
							</span>
							<span className="font-semibold text-amber-900 shrink-0">{direction.display}</span>
						</li>
					))}
				</ul>
			)}
		</MessageBox>
	);
}

export interface EditBookingWizardProps {
	booking: BookingEntity;
	/** True when there is a linked return leg that can also be altered. */
	isTwoWay: boolean;
	editOptions: EditOption[];
	onClose: () => void;
	/** Terminal action — routes the user into the relevant booking wizard tab. */
	onSelectEdit: (editType: EditType, alterReturn: boolean) => void;
}

// Brand-tinted rounded icon square, matching the booking detail section headers and the
// booking wizard step headers.
const iconSquareStyle: React.CSSProperties = {
	backgroundColor: 'color-mix(in srgb, var(--colour-brand-primary) 14%, transparent)',
	color: 'var(--colour-brand-primary)',
};

function IconSquare({ iconName }: { iconName: string }) {
	return (
		<span
			className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-[10px] shrink-0"
			style={iconSquareStyle}
			aria-hidden
		>
			<Icon name={iconName} useMaterialIcons classname="!text-[18px] sm:!text-[22px]" />
		</span>
	);
}

interface StepSelectTripsProps {
	booking: BookingEntity;
	alterReturn: boolean;
	onChange: (alterReturn: boolean) => void;
}

function StepSelectTrips({ booking, alterReturn, onChange }: StepSelectTripsProps) {
	const thisLeg = useLocationNamesFromBookingSummary(booking.bookedSummary);
	const returnBooking = booking.returnBooking ?? booking.returnBookingFor;
	const returnLeg = useLocationNamesFromBookingSummary(returnBooking?.bookedSummary);

	const formatRoute = (start?: string, end?: string) => (start && end ? `${start} → ${end}` : undefined);
	const thisRoute = formatRoute(thisLeg.startLocationName, thisLeg.endLocationName);
	const returnRoute = formatRoute(returnLeg.startLocationName, returnLeg.endLocationName);

	// "Both trips" is the default and shown first.
	const cards: { value: boolean; title: string; routes: string[]; fallback: string }[] = [
		{
			value: true,
			title: 'Both trips',
			routes: [thisRoute, returnRoute].filter((x): x is string => !!x),
			fallback: 'Apply the same changes to the return leg',
		},
		{
			value: false,
			title: 'This trip only',
			routes: [thisRoute].filter((x): x is string => !!x),
			fallback: 'Edit just this booking',
		},
	];

	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-lg font-semibold text-slate-900 m-0">Which trips are you editing?</h3>
				<div className="text-sm text-slate-500 mt-1">
					This booking has a linked return trip. Choose which trips your changes apply to.
				</div>
			</div>
			<div className="space-y-3">
				{cards.map(card => (
					<TripOptionCard
						key={card.title}
						selected={alterReturn === card.value}
						onClick={() => onChange(card.value)}
						title={card.title}
						routes={card.routes}
						fallback={card.fallback}
						testId={card.value ? 'edit-wizard-trips-both' : 'edit-wizard-trips-single'}
					/>
				))}
			</div>
		</div>
	);
}

// Earliest-first ordering of edit types by the booking wizard tab they open (see RedirectEditTo:
// passenger/trip-time → trip-selection tab, vehicle → vehicle, add-on → add-ons). Used to drop
// the user on the first wizard tab that holds something they chose to edit. Event types share a
// single tab, so their relative order is irrelevant.
const EDIT_TYPE_TAB_ORDER: EditType[] = [
	'passenger',
	// Grouped with 'passenger' rather than 'trip-time': all three land on the tickets tab on
	// desktop, but on mobile these two open the search step while 'trip-time' does not.
	'route-direction',
	'trip-time',
	'other',
	'vehicle',
	'passenger-details',
	// After 'passenger-details': both open tabs of the passenger flow, and the wizard runs
	// passengers → seating → details → add-ons.
	'seating',
	'add-on',
	'event-date-time',
	'event-contact',
	'tickets',
	'custom-ticket',
];

function pickFirstTabEditType(selected: EditType[]): EditType | undefined {
	// A type missing from the list sorts LAST, not first. `indexOf` answers -1, so ordering on it
	// raw put any unlisted type ahead of everything: 'seating' was unlisted, and an edit of
	// `Date or time` plus `Fare type or seat` opened on the seating step, past the date step the
	// booker had asked for. Landing too late is recoverable by stepping forward; landing too early
	// is not, because the wizard walks forward.
	const rank = (editType: EditType) => {
		const index = EDIT_TYPE_TAB_ORDER.indexOf(editType);
		return index === -1 ? EDIT_TYPE_TAB_ORDER.length : index;
	};
	return [...selected].sort((a, b) => rank(a) - rank(b))[0];
}

interface StepSelectWhatProps {
	booking: BookingEntity;
	editOptions: EditOption[];
	selectedTypes: EditType[];
	onToggle: (editType: EditType) => void;
}

function StepSelectWhat({ booking, editOptions, selectedTypes, onToggle }: StepSelectWhatProps) {
	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-lg font-semibold text-slate-900 m-0">What would you like to edit?</h3>
				<div className="text-sm text-slate-500 mt-1">Select everything you want to change.</div>
			</div>
			<div className="space-y-3">
				{editOptions.map(option => {
					const selected = selectedTypes.includes(option.editType);
					const showFeeNotice = DATE_TIME_EDIT_TYPES.includes(option.editType);
					const showFareTypeFeeNotice = FARE_TYPE_EDIT_TYPES.includes(option.editType);
					// Listed but not selectable, with the reason in place of the description — see
					// EditOption.disabledReason. A hidden option only generates a support call.
					const blocked = isNotNullOrUndefined(option.disabledReason);
					return (
						<div key={option.editType}>
							<button
								type="button"
								onClick={() => !blocked && onToggle(option.editType)}
								aria-pressed={selected}
								aria-disabled={blocked}
								disabled={blocked}
								data-test-id={`edit-wizard-option-${option.editType}`}
								className={`w-full text-left p-4 border rounded-xl transition-all
									flex items-center gap-4 ${
										blocked
											? 'border-slate-200 bg-slate-50 cursor-not-allowed'
											: `cursor-pointer ${
													selected
														? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500'
														: 'border-slate-200 hover:border-slate-300 bg-white'
												}`
									}`}
							>
								<IconSquare iconName={option.iconName} />
								<span className="flex flex-col flex-1 min-w-0">
									<span
										className={`text-sm sm:text-base font-semibold ${
											blocked ? 'text-slate-400' : 'text-slate-800'
										}`}
									>
										{option.label}
									</span>
									<span
										className={`text-[12px] mt-0.5 leading-snug ${
											blocked ? 'text-slate-500 italic' : 'text-slate-500'
										}`}
									>
										{option.disabledReason ?? option.description}
									</span>
								</span>
								<If condition={!blocked}>
									<span
										className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
											selected ? 'bg-sky-500 border-sky-500' : 'bg-white border-slate-300'
										}`}
									>
										{selected && (
											<Icon name="check" useMaterialIcons classname="text-white !text-[16px]" />
										)}
									</span>
								</If>
								<If condition={blocked}>
									<Icon name="lock" useMaterialIcons classname="text-slate-400 !text-[18px]" />
								</If>
							</button>
							{showFeeNotice && <DateTimeFeeNotice booking={booking} />}
							{showFareTypeFeeNotice && <FareTypeFeeNotice booking={booking} />}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export function EditBookingWizard({ booking, isTwoWay, editOptions, onClose, onSelectEdit }: EditBookingWizardProps) {
	const steps: ('trips' | 'what')[] = isTwoWay ? ['trips', 'what'] : ['what'];
	const totalSteps = steps.length;

	const [step, setStep] = React.useState(1);
	// Default to editing both trips only when there's an editable return leg (the 'trips' step is
	// shown and lets the user change this). When there isn't one — one-way bookings, or two-way
	// bookings whose return leg is cancelled/checked-in/closed — the 'trips' step is skipped, so
	// this default is what gets used: it must be false, otherwise the wizard pulls in the
	// non-editable return leg and wrongly assumes a two-way alteration, breaking the booking wizard.
	const [alterReturn, setAlterReturn] = React.useState(isTwoWay);
	const [selectedTypes, setSelectedTypes] = React.useState<EditType[]>([]);

	const stepIndex = Math.min(step, totalSteps) - 1;
	const currentStepId = steps[stepIndex];

	const goBack = () => {
		if (stepIndex === 0) {
			onClose();
			return;
		}
		setStep(s => s - 1);
	};

	const toggleType = (editType: EditType) => {
		setSelectedTypes(prev => (prev.includes(editType) ? prev.filter(x => x !== editType) : [...prev, editType]));
	};

	const handleConfirm = () => {
		const firstTabType = pickFirstTabEditType(selectedTypes);
		if (!firstTabType) {
			return;
		}
		// The wizard loads the whole booking, so landing on the earliest selected tab lets the
		// user reach every other thing they chose to edit by stepping through the wizard.
		onSelectEdit(firstTabType, alterReturn);
		onClose();
	};

	const footer = (
		<>
			<Button newStyles display={Display.Outline} colors={Colors.Grey} sizes={Sizes.Large} onClick={goBack}>
				{stepIndex === 0 ? 'Close' : 'Back'}
			</Button>
			{currentStepId === 'trips' && (
				<Button
					newStyles
					display={Display.Solid}
					colors={Colors.Secondary}
					sizes={Sizes.Large}
					onClick={() => setStep(s => Math.min(totalSteps, s + 1))}
					buttonProps={{ 'data-test-id': 'edit-wizard-continue' } as any}
				>
					Continue
				</Button>
			)}
			{currentStepId === 'what' && (
				<Button
					newStyles
					display={Display.Solid}
					colors={Colors.Secondary}
					sizes={Sizes.Large}
					disabled={selectedTypes.length === 0}
					onClick={handleConfirm}
					buttonProps={{ 'data-test-id': 'edit-wizard-confirm' } as any}
				>
					Continue
				</Button>
			)}
		</>
	);

	return (
		<WizardModal
			isOpen
			label="Edit booking"
			title="Edit booking"
			step={stepIndex + 1}
			totalSteps={totalSteps}
			onClose={onClose}
			footer={footer}
		>
			{currentStepId === 'trips' && (
				<StepSelectTrips booking={booking} alterReturn={alterReturn} onChange={setAlterReturn} />
			)}
			{currentStepId === 'what' && (
				<StepSelectWhat
					booking={booking}
					editOptions={editOptions}
					selectedTypes={selectedTypes}
					onToggle={toggleType}
				/>
			)}
		</WizardModal>
	);
}
